import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface RiskRow {
  readonly riskId: string;
  readonly riskCode: string;
  readonly riskTitle: string;
  readonly riskCategory: string;
  readonly riskLevel: string;
  readonly status: string;
  readonly requiresExecReview: boolean;
}

const CATEGORIES = ['', 'STRATEGIC', 'OPERATIONAL', 'FINANCIAL', 'COMPLIANCE', 'CYBER', 'DATA_PRIVACY', 'THIRD_PARTY', 'REPUTATIONAL', 'TECHNOLOGY', 'LEGAL'];
const LEVELS = ['', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
const CREATE_CATEGORIES = CATEGORIES.filter(Boolean);
const LIKELIHOODS = ['RARE', 'UNLIKELY', 'POSSIBLE', 'LIKELY', 'ALMOST_CERTAIN'];
const IMPACTS = ['INSIGNIFICANT', 'MINOR', 'MODERATE', 'MAJOR', 'SEVERE'];
const TREATMENTS = ['ACCEPT', 'MITIGATE', 'TRANSFER', 'AVOID'];

/**
 * M30 — Enterprise Risk Register (§15 / §31). Central register of security
 * risks with their category, computed risk level, treatment status and the
 * executive-review flag (BR-03: critical risks require executive review before
 * closure). Supports assess → mitigate → review lifecycle transitions — risk
 * capture, assessment, mitigation and review are wired to the guarded
 * /v1/security/risks write API.
 */
export function M30RiskRegisterPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<RiskRow[]>([]);
  const [riskCategory, setRiskCategory] = useState('');
  const [riskLevel, setRiskLevel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // create form
  const [title, setTitle] = useState('');
  const [newCategory, setNewCategory] = useState('CYBER');
  const [likelihood, setLikelihood] = useState('POSSIBLE');
  const [impact, setImpact] = useState('MODERATE');
  const [treatment, setTreatment] = useState('MITIGATE');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (riskCategory) params.set('riskCategory', riskCategory);
    if (riskLevel) params.set('riskLevel', riskLevel);
    const qs = params.toString();
    apiRequest<{ data: RiskRow[] }>(`/v1/security/risks${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, riskCategory, riskLevel, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m30.risk.notAuthorised', { verb, perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!title.trim()) { setActionError(tr('m30.risk.titleRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: RiskRow }>(`/v1/security/risks`, {
        method: 'POST',
        body: { riskTitle: title.trim(), riskCategory: newCategory },
        token,
      });
      setNotice(tr('m30.risk.registered', { code: r.data.riskCode }));
      setTitle('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m30.risk.verbRegister'), 'M30_RISK_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const runRowAction = async (
    fn: () => Promise<unknown>,
    successMsg: string,
    verb: string,
    perm: string,
  ): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const assess = (r: RiskRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/security/risks/${r.riskId}/assess`, { method: 'POST', body: { likelihood, impact }, token }),
      tr('m30.risk.assessed', { code: r.riskCode }), tr('m30.risk.verbAssess'), 'M30_RISK_MANAGE',
    );

  const mitigate = (r: RiskRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/security/risks/${r.riskId}/mitigate`, { method: 'POST', body: { treatmentStrategy: treatment, mitigationPlan: `Mitigation plan for ${r.riskCode}` }, token }),
      tr('m30.risk.mitigated', { code: r.riskCode }), tr('m30.risk.verbMitigate'), 'M30_RISK_MANAGE',
    );

  const review = (r: RiskRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/security/risks/${r.riskId}/review`, { method: 'POST', body: {}, token }),
      tr('m30.risk.reviewed', { code: r.riskCode }), tr('m30.risk.verbReview'), 'M30_RISK_MANAGE',
    );

  return (
    <main aria-label="risk-register">
      <h1>{tr('m30.risk.title')}</h1>

      <section aria-label="create-risk">
        <h2>{tr('m30.risk.registerHeading')}</h2>
        <form aria-label="risk-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m30.risk.fieldTitle')}<input aria-label="field-risk-title" value={title} onChange={(e) => setTitle(e.target.value)} /></label>
          <label>{tr('m30.risk.fieldCategory')}
            <select aria-label="field-risk-category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
              {CREATE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-risk" disabled={busy}>{busy ? tr('m30.risk.submitRegistering') : tr('m30.risk.submitRegister')}</button>
        </form>
        <div className="assessment-controls">
          <label>{tr('m30.risk.fieldLikelihood')}
            <select aria-label="field-likelihood" value={likelihood} onChange={(e) => setLikelihood(e.target.value)}>
              {LIKELIHOODS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
          <label>{tr('m30.risk.fieldImpact')}
            <select aria-label="field-impact" value={impact} onChange={(e) => setImpact(e.target.value)}>
              {IMPACTS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </label>
          <label>{tr('m30.risk.fieldTreatment')}
            <select aria-label="field-treatment" value={treatment} onChange={(e) => setTreatment(e.target.value)}>
              {TREATMENTS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
        </div>
        {actionError && <p role="alert" aria-label="risk-error">{actionError}</p>}
        {notice && <p aria-label="risk-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m30.risk.filterCategory')}
          <select aria-label="filter-risk-category" value={riskCategory} onChange={(e) => setRiskCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c || 'all'} value={c}>{c || tr('m30.risk.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m30.risk.filterLevel')}
          <select aria-label="filter-risk-level" value={riskLevel} onChange={(e) => setRiskLevel(e.target.value)}>
            {LEVELS.map((l) => <option key={l || 'all'} value={l}>{l || tr('m30.risk.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m30.risk.loading')}</p> : (
        <>
          <p aria-label="risk-count">{tr('m30.risk.count', { count: rows.length })}</p>
          <table aria-label="risk-table">
            <thead><tr><th>{tr('m30.risk.thCode')}</th><th>{tr('m30.risk.thTitle')}</th><th>{tr('m30.risk.thCategory')}</th><th>{tr('m30.risk.thLevel')}</th><th>{tr('m30.risk.thStatus')}</th><th>{tr('m30.risk.thExecReview')}</th><th>{tr('m30.risk.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.riskId}>
                  <td>{r.riskCode}</td><td>{r.riskTitle}</td><td>{r.riskCategory}</td>
                  <td>{r.riskLevel}</td><td>{r.status}</td>
                  <td>{r.requiresExecReview ? tr('m30.risk.execRequired') : tr('m30.risk.execNone')}</td>
                  <td>
                    <button type="button" aria-label={`assess-${r.riskCode}`} onClick={() => void assess(r)}>{tr('m30.risk.btnAssess')}</button>
                    <button type="button" aria-label={`mitigate-${r.riskCode}`} onClick={() => void mitigate(r)}>{tr('m30.risk.btnMitigate')}</button>
                    <button type="button" aria-label={`review-${r.riskCode}`} onClick={() => void review(r)}>{tr('m30.risk.btnReview')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
